import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
} from "@aws-amplify/auth";
import { Amplify } from "aws-amplify";
import { cognitoConfig } from "../config/cognito";
import { userBootstrapApi } from "../api/bootstrap";

// Configure Amplify Auth
Amplify.configure({
  Auth: cognitoConfig,
});

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any | null;
  signIn: (username: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // console.log("🔄 AuthContext Mounted / Re-Checked Authentication");
  }, []);

  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      userBootstrapApi.clear();
      // First check if we have a valid session
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error("No valid session");
      }

      // If we have a valid session, get the user details
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      setIsAuthenticated(true);
      setUser({ ...currentUser, attributes });
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Check auth state on mount and route changes
  useEffect(() => {
    checkAuthState();
  }, []);

  const handleSignIn = async (username: string, password: string) => {
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });
      if (isSignedIn) {
        userBootstrapApi.clear();
        await checkAuthState(); // Re-check auth state after successful sign in
        return user;
      }
      return nextStep;
    } catch (error) {
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      userBootstrapApi.clear();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      throw error;
    }
  };

  if (isLoading) {
    return null; // or a loading spinner
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        signIn: handleSignIn,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
